import { useState, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { LazyLoadImage as BaseLazyLoadImage } from 'react-lazy-load-image-component'

const LazyLoadImage = BaseLazyLoadImage as any

import PlaceHolder from '@/presentation/assets/img/PlaceHolder.png'
import ArrowRight from '@/presentation/assets/svg/ArrowRight.svg'
import CircleAttention from '@/presentation/assets/svg/CircleAttention.svg'
import CircleCheck from '@/presentation/assets/svg/CircleCheck.svg'
import Eye from '@/presentation/assets/svg/Eye.svg'
import ImageIcon from '@/presentation/assets/svg/Image.svg'
import MapMark from '@/presentation/assets/svg/MapMark.svg'
import { ROUTES } from '@/services/constants/routes'
import useNavigateWithOrderCode from '@/services/hooks/useNavigateWithOrderCode'

interface RoomItemProps {
  id: string
  image: string
  roomId: string
  name: string
  small: boolean
  photosQuantity: number
  state: boolean
  isCurrentItemInView?: boolean
  visitedByUser?: boolean
  alteration: boolean
}

const RoomItem: React.FC<RoomItemProps> = ({
  id,
  image,
  roomId,
  name,
  small,
  photosQuantity,
  state,
  isCurrentItemInView,
  visitedByUser,
  alteration
}) => {
  const { t } = useTranslation()

  const navigate = useNavigateWithOrderCode()

  const [imageLoaded, setImageLoaded] = useState(false)

  const handleItemClick = () => {
    switch (id) {
      case 'building':
        navigate(`${ROUTES.ROOM_ITEM}/0/building`, alteration)
        break

      case 'general':
        navigate(`${ROUTES.ROOM_ITEM}/${roomId}/general`, alteration)
        break

      default:
        navigate(`${ROUTES.ROOM_ITEM}/${roomId}/${id}`, alteration)
    }
  }

  useEffect(() => {
    const preLoadImage = () => {
      if (!image) return
      const img = new Image()
      img.src = image
      img.onload = () => {
        setImageLoaded(true)
      }
    }
    if (!imageLoaded) {
      preLoadImage()
    }
  }, [imageLoaded, image])

  return (
    <div
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={`max-w-[${small ? 240 : 320}px] ${small ? 'rounded-lg' : 'rounded-xl'} relative flex w-[300px] shrink-0 cursor-pointer flex-col overflow-hidden border border-gray-400 shadow-md`}
      onClick={handleItemClick}
    >
      {isCurrentItemInView ? (
        <div className="absolute left-0 top-0 flex w-full items-center justify-center gap-x-2 bg-primary p-1">
          <img src={MapMark} />

          <span className="text-sm text-white">
            {t('YOU_ARE_HERE', 'Você está aqui')}
          </span>
        </div>
      ) : (
        !!visitedByUser && (
          <div className="absolute left-0 top-0 flex h-[80px] w-full flex-col items-center justify-center gap-y-px bg-black opacity-40">
            <img src={Eye} />

            <span className="text-base text-white">{t('SEEN', 'Visto')}</span>
          </div>
        )
      )}

      {image && imageLoaded ? (
        <LazyLoadImage
          className="h-20 w-full bg-cover bg-center bg-no-repeat"
          src={image}
          alt={image}
          width="100%"
        />
      ) : (
        <div className="h-20 w-full">
          <img src={PlaceHolder} />
        </div>
      )}

      <div
        className={`flex h-full flex-col rounded-t-none py-2 ${small ? 'rounded-lg p-2' : 'rounded-xl p-4'} `}
      >
        <div className="flex justify-between">
          <span className="text-lg font-bold text-gray-600">{name || ''}</span>

          <img className="h-[30px]" src={ArrowRight} />
        </div>

        <div className={`flex items-center gap-2`}>
          <img src={ImageIcon} />

          <span className="text-gray-500">
            {photosQuantity || 0} {t('PHOTOS', 'fotos')}
          </span>
        </div>

        <div className={`flex items-center gap-1 ${!small && 'mt-1'}`}>
          {state ? (
            <>
              <img src={CircleAttention} />

              <span className="text-error">
                {t('WITH_OBSERVATIONS', 'Com observações')}
              </span>
            </>
          ) : (
            <>
              <img src={CircleCheck} />

              <span className="text-success">
                {t('WITHOUT_OBSERVATIONS', 'Sem observações')}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default RoomItem
